import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import AuthGuard from './components/AuthGuard';
import AuthHashHandler from './components/AuthHashHandler';
import { TenantProvider } from './contexts/TenantContext';
import MainLayout from './components/MainLayout';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import Appointments from './pages/Appointments';
import Doctors from './pages/Doctors';
import Billing from './pages/Billing';
import Reports from './pages/Reports';
import Settings from './pages/Settings/index';
import UserManagement from './pages/UserManagement';
import PharmacyOverview from './modules/pharmacy/pages/PharmacyOverview';
import Medicines from './modules/pharmacy/pages/Medicines';
import Stock from './modules/pharmacy/pages/Stock';
import Suppliers from './modules/pharmacy/pages/Suppliers';
import Purchases from './modules/pharmacy/pages/Purchases';
import CreatePurchase from './modules/pharmacy/pages/CreatePurchase';
import Dispense from './modules/pharmacy/pages/Dispense';
import Sales from './modules/pharmacy/pages/Sales';
import PharmacyPOS from './pages/pharmacy/PharmacyPOS';
import Expenses from './modules/pharmacy/pages/Expenses';
import AddExpense from './modules/pharmacy/pages/AddExpense';
import ExpenseCategories from './modules/pharmacy/pages/ExpenseCategories';
import Units from './modules/pharmacy/pages/Units';
import PharmacyReports from './modules/pharmacy/pages/PharmacyReports';
import ConsultationWorklist from './modules/consultation/pages/ConsultationWorklist';
import NewConsultation from './modules/consultation/pages/NewConsultation';
import ConsultationView from './modules/consultation/pages/ConsultationView';
import PrescriptionPrint from './modules/consultation/pages/PrescriptionPrint';
import Orders from './modules/consultation/pages/Orders';
import OrderNew from './modules/consultation/pages/OrderNew';
import OrderDetail from './modules/consultation/pages/OrderDetail';
import ReferralOverview from './modules/referrals/pages/ReferralOverview';
import InReferrals from './modules/referrals/pages/InReferrals';
import OutReferrals from './modules/referrals/pages/OutReferrals';
import NewReferral from './modules/referrals/pages/NewReferral';
import ReferralView from './modules/referrals/pages/ReferralView';
import Partners from './modules/referrals/pages/Partners';
import Commissions from './modules/referrals/pages/Commissions';
import ReferralPolicies from './modules/referrals/pages/Policies';
import PublicReferralForm from './modules/referrals/pages/PublicReferralForm';
import StaffList from './modules/staff/pages/StaffList';
import StaffEditor from './modules/staff/pages/StaffEditor';
import StaffProfilePage from './modules/staff/pages/StaffProfilePage';
import AccountList from './modules/staff/pages/AccountList';
import AccountEditor from './modules/staff/pages/AccountEditor';
import AccountView from './modules/staff/pages/AccountView';
import AccountImport from './modules/staff/pages/AccountImport';
import PrescriptionList from './modules/doctor/pages/PrescriptionList';
import PrescriptionNew from './modules/doctor/pages/PrescriptionNew';
import PrescriptionView from './modules/doctor/pages/PrescriptionView';
import InsuranceOverview from './modules/insurance/pages/InsuranceOverview';
import InsurancePolicies from './modules/insurance/pages/Policies';
import Claims from './modules/insurance/pages/Claims';
import HROverview from './modules/hr/pages/HROverview';
import Roster from './modules/hr/pages/Roster';
import Payroll from './modules/hr/pages/Payroll';
import InventoryOverview from './modules/inventory/pages/InventoryOverview';
import PreAuthorizations from './modules/insurance/pages/PreAuthorizations';
import Attendance from './modules/hr/pages/Attendance';
import Items from './modules/inventory/pages/Items';
import Vendors from './modules/inventory/pages/Vendors';
import PurchaseOrders from './modules/inventory/pages/PurchaseOrders';

import PayrollOverview from './modules/payroll/pages/PayrollOverview';
import PayStructuresPage from './modules/payroll/pages/PayStructuresPage';
import PayrollRunsPage from './modules/payroll/pages/PayrollRunsPage';
import PayslipsListPage from './modules/payroll/pages/PayslipsListPage';
import PaymentsListPage from './modules/payroll/pages/PaymentsListPage';
import LabWorklist from './pages/Lab/Worklist';
import LabTestCatalog from './pages/Lab/TestCatalog';
import LabNewOrder from './pages/Lab/NewOrder';
import LabOrderDetails from './pages/Lab/OrderDetails';
import LabResultEntry from './pages/Lab/ResultEntry';
import LabVerifyFinalize from './pages/Lab/VerifyFinalize';
import LabReportViewer from './pages/Lab/ReportViewer';
import LabPublicVerify from './pages/Lab/PublicVerify';
import LabAnalyzerImport from './pages/Lab/AnalyzerImport';
import LabTATDashboard from './pages/Lab/TATDashboard';
import LabReagentInventory from './pages/Lab/ReagentInventory';
import NotificationLog from './pages/Lab/NotificationLog';
import RadiologyWorklist from './pages/Radiology/Worklist';
import RadiologyNewOrder from './pages/Radiology/NewOrder';
import RadiologyTestCatalog from './pages/Radiology/TestCatalog';
import RadiologyReportEditor from './pages/Radiology/ReportEditor';
import RadiologyReportViewer from './pages/Radiology/ReportViewer';
import RadiologyTATDashboard from './pages/Radiology/TATDashboard';
import RadiologyPublicVerify from './pages/Radiology/PublicVerify';
import DoctorResults from './pages/Doctor/Results';
import AdmissionList from './pages/IPD/AdmissionList';
import NewAdmission from './pages/IPD/NewAdmission';
import AdmissionDetails from './pages/IPD/AdmissionDetails';
import BedManagement from './pages/IPD/BedManagement';
import DischargeSummaryForm from './pages/IPD/DischargeSummaryForm';
import DischargeSummaryViewer from './pages/IPD/DischargeSummaryViewer';
import PublicVerifyDischarge from './pages/IPD/PublicVerifyDischarge';
import DischargedList from './pages/IPD/DischargedList';
import DischargedBilling from './pages/IPD/DischargedBilling';
import ServiceCatalog from './pages/OPD/ServiceCatalog';
import ReferralContracts from './pages/OPD/ReferralContracts';
import ReferralPayouts from './pages/OPD/ReferralPayouts';
import NotificationPreferences from './pages/Settings/NotificationPreferences';
import InsuranceClaimsList from './pages/Insurance/ClaimsList';
import InsuranceClaimForm from './pages/Insurance/ClaimForm';
import AccountsManagement from './pages/AccountsManagement';
import PaymentHistory from './pages/PaymentHistory';
import ReceiptManagement from './pages/ReceiptManagement';
import ReportsPrint from './pages/ReportsPrint';
import UserRoles from './pages/UserRoles';
import UsersManagement from './pages/UsersManagement';
import DoctorsManage from './pages/Doctors/Manage';
import DoctorsDepartments from './pages/Doctors/Departments';
import DoctorsBloodGroups from './pages/Doctors/BloodGroups';
import DoctorsPCDoctors from './pages/Doctors/PCDoctors';
import DoctorsReportDoctors from './pages/Doctors/ReportDoctors';
import DoctorsSchedules from './pages/Doctors/Schedules';
import DoctorsBreaks from './pages/Doctors/Breaks';
import DoctorsHolidays from './pages/Doctors/Holidays';
import PatientReportCheck from './pages/PatientInfo/ReportCheck';
import CasePicture from './pages/PatientInfo/CasePicture';
import ReportPicture from './pages/PatientInfo/ReportPicture';
import BulkSMSSend from './pages/PatientInfo/BulkSMSSend';
import SMSOthersNumber from './pages/PatientInfo/SMSOthersNumber';
import BulkSMSHistory from './pages/PatientInfo/BulkSMSHistory';
import AppointmentReport from './pages/Appointments/Report';
import CreateAppointment from './pages/Appointments/Create';
import BillingsAccount from './pages/Billings/Account';
import BillingsTransactionHead from './pages/Billings/TransactionHead';
import BillingsSetTestPrice from './pages/Billings/SetTestPrice';
import BillingsInvoices from './pages/Billings/Invoices';
import InvoiceForm from './pages/Billings/InvoiceForm';
import InvoicePrint from './pages/Billings/InvoicePrint';
import BillingsDueCollection from './pages/Billings/DueCollection';
import BillingsDueReport from './pages/Billings/DueReport';
import BillingsInvoiceReport from './pages/Billings/InvoiceReport';
import BillingsPCInvoiceReport from './pages/Billings/PCInvoiceReport';
import BillingsPCInvoiceDateGroup from './pages/Billings/PCInvoiceDateGroup';
import BillingsCashCollectionReport from './pages/Billings/CashCollectionReport';
import BillingsDueCollectionReport from './pages/Billings/DueCollectionReport';
import BillingsDoctorWiseReport from './pages/Billings/DoctorWiseReport';
import BillingsCounterWiseReport from './pages/Billings/CounterWiseReport';
import BillingsTestReport from './pages/Billings/TestReport';
import BillingsVoucherHead from './pages/Billings/VoucherHead';
import BillingsDebitVoucher from './pages/Billings/DebitVoucher';
import BillingsDebitVCheck from './pages/Billings/DebitVCheck';
import BillingsCreditVoucher from './pages/Billings/CreditVoucher';
import BillingsPCPayment from './pages/Billings/PCPayment';
import BillingsPaymentReports from './pages/Billings/PaymentReports';
import BillingsGeneralLedger from './pages/Billings/GeneralLedger';
import BillingsDailyCashbook from './pages/Billings/DailyCashbook';
import BillingsSummaryReport from './pages/Billings/SummaryReport';
import BillingsExpenseSummary from './pages/Billings/ExpenseSummary';
import BillingsCommissionReports from './pages/Billings/CommissionReports';
import BillingsBalanceTransfer from './pages/Billings/BalanceTransfer';
import BillingsFundManages from './pages/Billings/FundManages';
import BillingsIndex from './pages/Billings/index';
import Subscription from './modules/softwarebilling/pages/Subscription';
import PaymentNow from './modules/softwarebilling/pages/PaymentNow';
import SmsBuy from './modules/softwarebilling/pages/SmsBuy';
import SmsHistory from './modules/softwarebilling/pages/SmsHistory';
import DiagnosisCategories from './pages/Diagnosis/DiagnosisCategories';
import TestName from './pages/Diagnosis/TestName';
import PathologySetup from './pages/Diagnosis/PathologySetup';
import PathologyLabReport from './pages/Diagnosis/PathologyLabReport';
import MolecularPathSetup from './pages/Diagnosis/MolecularPathSetup';
import MolecularPathReport from './pages/Diagnosis/MolecularPathReport';
import USGSetup from './pages/Diagnosis/USGSetup';
import USGReport from './pages/Diagnosis/USGReport';
import XRayReport from './pages/Diagnosis/XRayReport';
import AuthCallback from './pages/AuthCallback';
import SetPassword from './pages/SetPassword';

function App() {
  return (
    <>
      <Toaster position="top-right" />
      <Router>
        <AuthHashHandler />
        <TenantProvider>
          <Routes>
            <Route path="/_ping" element={<div>OK</div>} />
            <Route path="r/:code" element={<PublicReferralForm />} />
            <Route path="/*" element={
              <AuthGuard>
                <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="patients" element={<Patients />} />
                  <Route path="patients/report-check" element={<PatientReportCheck />} />
                  <Route path="patients/case-picture" element={<CasePicture />} />
                  <Route path="patients/report-picture" element={<ReportPicture />} />
                  <Route path="patients/bulk-sms" element={<BulkSMSSend />} />
                  <Route path="patients/sms-others" element={<SMSOthersNumber />} />
                  <Route path="patients/sms-history" element={<BulkSMSHistory />} />
                  <Route path="appointments" element={<Appointments />} />
                  <Route path="appointments/create" element={<CreateAppointment />} />
                  <Route path="appointments/report" element={<AppointmentReport />} />
                  
                  {/* OPD Routes */}
                  <Route path="opd/services" element={<ServiceCatalog />} />
                  <Route path="opd/referrals" element={<ReferralContracts />} />
                  <Route path="opd/payouts" element={<ReferralPayouts />} />
                  <Route path="settings/notifications" element={<NotificationPreferences />} />
                  
                  {/* Doctor Routes */}
                  <Route path="doctors" element={<Doctors />} />
                  <Route path="doctors/manage" element={<DoctorsManage />} />
                  <Route path="doctors/departments" element={<DoctorsDepartments />} />
                  <Route path="doctors/blood-groups" element={<DoctorsBloodGroups />} />
                  <Route path="doctors/pc-doctors" element={<DoctorsPCDoctors />} />
                  <Route path="doctors/report-doctors" element={<DoctorsReportDoctors />} />
                  <Route path="doctors/schedules" element={<DoctorsSchedules />} />
                  <Route path="doctors/breaks" element={<DoctorsBreaks />} />
                  <Route path="doctors/holidays" element={<DoctorsHolidays />} />
                  <Route path="staff" element={<StaffList />} />
                  <Route path="staff/new" element={<StaffEditor />} />
                  <Route path="staff/:id" element={<StaffProfilePage />} />
                  <Route path="staff/:id/edit" element={<StaffEditor />} />
                  <Route path="staff/accounts" element={<AccountList />} />
                  <Route path="staff/accounts/new" element={<AccountEditor />} />
                  <Route path="staff/accounts/:id" element={<AccountView />} />
                  <Route path="staff/accounts/import" element={<AccountImport />} />
                  
                  {/* Legacy Billing Route - Redirect to Billings */}
                  <Route path="billing" element={<Billing />} />
                  
                  {/* Billings/Reception Routes */}
                  <Route path="billings" element={<BillingsIndex />} />
                  <Route path="billings/account" element={<BillingsAccount />} />
                  <Route path="billings/transaction-head" element={<BillingsTransactionHead />} />
                  <Route path="billings/set-test-price" element={<BillingsSetTestPrice />} />
                  <Route path="billings/invoices" element={<BillingsInvoices />} />
                  <Route path="billings/invoices/new" element={<InvoiceForm />} />
                  <Route path="billings/invoices/:id/edit" element={<InvoiceForm />} />
                  <Route path="billings/invoices/:id/print" element={<InvoicePrint />} />
                  <Route path="billings/due-collection" element={<BillingsDueCollection />} />
                  <Route path="billings/due-report" element={<BillingsDueReport />} />
                  <Route path="billings/invoice-report" element={<BillingsInvoiceReport />} />
                  <Route path="billings/pc-invoice-report" element={<BillingsPCInvoiceReport />} />
                  <Route path="billings/pc-invoice-date-group" element={<BillingsPCInvoiceDateGroup />} />
                  <Route path="billings/cash-collection-report" element={<BillingsCashCollectionReport />} />
                  <Route path="billings/due-collection-report" element={<BillingsDueCollectionReport />} />
                  <Route path="billings/doctor-wise-report" element={<BillingsDoctorWiseReport />} />
                  <Route path="billings/counter-wise-report" element={<BillingsCounterWiseReport />} />
                  <Route path="billings/test-report" element={<BillingsTestReport />} />
                  <Route path="billings/voucher-head" element={<BillingsVoucherHead />} />
                  <Route path="billings/debit-voucher" element={<BillingsDebitVoucher />} />
                  <Route path="billings/debit-v-check" element={<BillingsDebitVCheck />} />
                  <Route path="billings/credit-voucher" element={<BillingsCreditVoucher />} />
                  <Route path="billings/pc-payment" element={<BillingsPCPayment />} />
                  <Route path="billings/payment-reports" element={<BillingsPaymentReports />} />
                  <Route path="billings/general-ledger" element={<BillingsGeneralLedger />} />
                  <Route path="billings/daily-cashbook" element={<BillingsDailyCashbook />} />
                  <Route path="billings/summary-report" element={<BillingsSummaryReport />} />
                  <Route path="billings/expense-summary" element={<BillingsExpenseSummary />} />
                  <Route path="billings/commission-reports" element={<BillingsCommissionReports />} />
                  <Route path="billings/balance-transfer" element={<BillingsBalanceTransfer />} />
                  <Route path="billings/fund-manages" element={<BillingsFundManages />} />
                  
                  {/* Legacy Accounts Management Routes - Keep for backward compatibility */}
                  <Route path="accounts" element={<AccountsManagement />} />
                  <Route path="accounts/payment-history" element={<PaymentHistory />} />
                  <Route path="accounts/receipts" element={<ReceiptManagement />} />
                  
                  <Route path="reports" element={<Reports />} />
                  <Route path="reports/print" element={<ReportsPrint />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="settings/users" element={<UserManagement />} />
                  <Route path="settings/user-roles" element={<UserRoles />} />
                  <Route path="settings/users-management" element={<UsersManagement />} />
                  
                  {/* Software Billing Routes */}
                  <Route path="softwarebilling/subscription" element={<Subscription />} />
                  <Route path="softwarebilling/paymentnow" element={<PaymentNow />} />
                  <Route path="softwarebilling/smsbuy" element={<SmsBuy />} />
                  <Route path="softwarebilling/smshistory" element={<SmsHistory />} />
                  
                  <Route path="pharmacy" element={<PharmacyOverview />} />
                  <Route path="pharmacy/sales" element={<Sales />} />
                  <Route path="pharmacy/pos" element={<PharmacyPOS />} />
                  <Route path="pharmacy/pos-new" element={<PharmacyPOS />} />
                  <Route path="pharmacy/expenses" element={<Expenses />} />
                  <Route path="pharmacy/expenses/add" element={<AddExpense />} />
                  <Route path="pharmacy/expense-categories" element={<ExpenseCategories />} />
                  <Route path="pharmacy/reports" element={<PharmacyReports />} />
                  <Route path="pharmacy/medicines" element={<Medicines />} />
                  <Route path="pharmacy/suppliers" element={<Suppliers />} />
                  <Route path="pharmacy/units" element={<Units />} />
                  <Route path="pharmacy/stock" element={<Stock />} />
                  <Route path="pharmacy/purchases" element={<Purchases />} />
                  <Route path="pharmacy/purchases/new" element={<CreatePurchase />} />
                  <Route path="pharmacy/dispense" element={<Dispense />} />
                  <Route path="consultations" element={<ConsultationWorklist />} />
                  <Route path="consultations/new" element={<NewConsultation />} />
                  <Route path="consultations/:id" element={<ConsultationView />} />
                  <Route path="prescriptions/:id/print" element={<PrescriptionPrint />} />
                  <Route path="doctor/prescriptions" element={<PrescriptionList />} />
                  <Route path="doctor/prescriptions/new" element={<PrescriptionNew />} />
                  <Route path="doctor/prescriptions/:id" element={<PrescriptionView />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/new" element={<OrderNew />} />
                  <Route path="orders/:id" element={<OrderDetail />} />
                  <Route path="referrals" element={<ReferralOverview />} />
                  <Route path="referrals/in" element={<InReferrals />} />
                  <Route path="referrals/out" element={<OutReferrals />} />
                  <Route path="referrals/new" element={<NewReferral />} />
                  <Route path="referrals/:id" element={<ReferralView />} />
                  <Route path="referrals/partners" element={<Partners />} />
                  <Route path="referrals/commissions" element={<Commissions />} />
                  <Route path="referrals/policies" element={<ReferralPolicies />} />
                  <Route path="insurance" element={<InsuranceOverview />} />
                  <Route path="insurance/policies" element={<InsurancePolicies />} />
                  <Route path="insurance/preauth" element={<PreAuthorizations />} />
                  <Route path="insurance/claims" element={<Claims />} />
                  <Route path="hr" element={<HROverview />} />
                  <Route path="hr/roster" element={<Roster />} />
                  <Route path="hr/attendance" element={<Attendance />} />
                  <Route path="hr/payroll" element={<Payroll />} />
                  <Route path="inventory" element={<InventoryOverview />} />
                  <Route path="inventory/items" element={<Items />} />
                  <Route path="inventory/vendors" element={<Vendors />} />
                  <Route path="inventory/purchase-orders" element={<PurchaseOrders />} />
                  
                  {/* Payroll Routes */}
                  <Route path="payroll" element={<PayrollOverview />} />
                  <Route path="payroll/setup/structures" element={<PayStructuresPage />} />
                  <Route path="payroll/runs" element={<PayrollRunsPage />} />
                  <Route path="payroll/slips" element={<PayslipsListPage />} />
                  <Route path="payroll/payments" element={<PaymentsListPage />} />
                  
                  {/* Lab Routes */}
                  <Route path="lab" element={<LabWorklist />} />
                  <Route path="lab/catalog" element={<LabTestCatalog />} />
                  <Route path="lab/orders/new" element={<LabNewOrder />} />
                  <Route path="lab/orders/:orderId" element={<LabOrderDetails />} />
                  <Route path="lab/orders/:orderId/results" element={<LabResultEntry />} />
                  <Route path="lab/orders/:orderId/verify" element={<LabVerifyFinalize />} />
                  <Route path="lab/orders/:orderId/report" element={<LabReportViewer />} />
                  <Route path="lab/analyzer-import" element={<LabAnalyzerImport />} />
                  <Route path="lab/tat" element={<LabTATDashboard />} />
                  <Route path="lab/reagents" element={<LabReagentInventory />} />
                  <Route path="lab/notifications" element={<NotificationLog />} />
                  
                  {/* Radiology Routes */}
                  <Route path="radiology" element={<RadiologyWorklist />} />
                  <Route path="radiology/new" element={<RadiologyNewOrder />} />
                  <Route path="radiology/catalog" element={<RadiologyTestCatalog />} />
                  <Route path="radiology/report/:orderId" element={<RadiologyReportEditor />} />
                  <Route path="radiology/reports/:id" element={<RadiologyReportViewer />} />
                  <Route path="radiology/tat" element={<RadiologyTATDashboard />} />
                  
                  {/* Diagnosis Routes */}
                  <Route path="diagnosis/categories" element={<DiagnosisCategories />} />
                  <Route path="diagnosis/test-names" element={<TestName />} />
                  <Route path="diagnosis/pathology-setup" element={<PathologySetup />} />
                  <Route path="diagnosis/pathology-reports" element={<PathologyLabReport />} />
                  <Route path="diagnosis/molecular-setup" element={<MolecularPathSetup />} />
                  <Route path="diagnosis/molecular-reports" element={<MolecularPathReport />} />
                  <Route path="diagnosis/usg-setup" element={<USGSetup />} />
                  <Route path="diagnosis/usg-reports" element={<USGReport />} />
                  <Route path="diagnosis/xray-reports" element={<XRayReport />} />
                  
                  {/* Doctor Portal Routes */}
                  <Route path="doctor/results" element={<DoctorResults />} />
                  
                  {/* IPD Routes */}
                  <Route path="ipd" element={<AdmissionList />} />
                  <Route path="ipd/new" element={<NewAdmission />} />
                  <Route path="ipd/:admissionId" element={<AdmissionDetails />} />
                  <Route path="ipd/beds" element={<BedManagement />} />
                  <Route path="ipd/discharge" element={<DischargeSummaryForm />} />
                  <Route path="ipd/discharge/:summaryId" element={<DischargeSummaryViewer />} />
                  <Route path="ipd/discharged" element={<DischargedList />} />
                  <Route path="ipd/discharged-billing" element={<DischargedBilling />} />
                  
                  {/* Insurance Routes */}
                  <Route path="insurance/claims" element={<InsuranceClaimsList />} />
                  <Route path="insurance/claims/new" element={<InsuranceClaimForm />} />
                </Route>
              </Routes>
            </AuthGuard>
          } />
          {/* Public Verify Routes (no auth required) */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/set-password" element={<SetPassword />} />
          <Route path="/verify/lab/:reportId" element={<LabPublicVerify />} />
          <Route path="/verify/rad/:reportId" element={<RadiologyPublicVerify />} />
          <Route path="/verify/discharge/:summaryNo" element={<PublicVerifyDischarge />} />
        </Routes>
        </TenantProvider>
      </Router>
    </>
  );
}

export default App;
